import { onMount, onDestroy } from '@/modules/_'
import gsap from '@lib/gsap'
import { Scroll } from '@lib/scroll'
import { MenuController } from '@lib/menu-controller'

export default function (element: HTMLElement, dataset: DOMStringMap) {
  onMount(() => {
    let isActive = false
    let currentTl: gsap.core.Timeline | null = null
    let menuControllerUnregister: (() => void) | null = null

    const defaults = {
      ease: 'expo.out',
      duration: 1,
    }

    const triggerOpen = element.querySelector('[menu=trigger-open]')
    const triggerClose = element.querySelector('[menu=trigger-close]')
    const menuContainer = element.querySelector('[menu=container]')
    const menuContent = element.querySelectorAll('[menu=content]')
    const mainPage = document.querySelector('[data-taxi]') as HTMLElement

    gsap.set(menuContainer, {
      clipPath: 'polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)',
      display: 'flex',
    })

    gsap.set(menuContent, { y: '50%', opacity: 0.25 })

    const toggleMenu = () => {
      if (isActive) {
        closeMenu()
      } else {
        openMenu()
      }
    }

    const handleTriggerClick = (e: Event) => {
      e.preventDefault()
      toggleMenu()
    }

    const openMenu = () => {
      if (isActive) return

      isActive = true
      disableTriggers()

      Scroll.stop()
      setScrollPosition()

      // Kill any existing timeline
      if (currentTl) {
        currentTl.kill()
      }

      const openTl = gsap.timeline({
        paused: true,
        defaults,
      })
      currentTl = openTl

      openTl.to(mainPage, {
        y: '-40%',
        opacity: 0.25,
      })

      openTl.to(
        menuContainer,
        {
          clipPath: 'polygon(0% 100%, 100% 100%, 100% 0%, 0% 0%)',
          onComplete: () => {
            gsap.set(mainPage, { y: '100px' })

            enableTriggers()
          },
        },
        '<'
      )

      openTl.to(
        menuContent,
        {
          y: '0%',
          opacity: 1,
        },
        '<'
      )

      openTl.play()
    }

    const closeMenu = (isPageOut: boolean = false) => {
      if (!isActive) return

      disableTriggers()

      if (currentTl) {
        currentTl.kill()
      }

      if (!isPageOut) {
        Scroll.start()
      }

      gsap.set(mainPage, { y: 100 })
      // Scroll.toTop();

      const closeTl = gsap.timeline({
        paused: true,
        defaults: {
          ...defaults,
          duration: isPageOut ? 1.2 : defaults.duration,
        },
        onComplete: () => {
          gsap.set(menuContainer, {
            clipPath: 'polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)',
          })

          isActive = false
          enableTriggers()
          resetScrollPosition()

          if (isPageOut) {
            Scroll.start()
          }

          gsap.set(menuContent, { y: '50%', opacity: 0.25 })
        },
      })
      currentTl = closeTl

      closeTl.to(
        mainPage,
        {
          y: 0,
          opacity: 1,
        },
        '<'
      )

      closeTl.to(
        menuContainer,
        {
          clipPath: 'polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)',
        },
        '<'
      )

      closeTl.to(
        menuContent,
        {
          y: '-100%',
          opacity: 0.25,
        },
        '<'
      )

      closeTl.play()
    }

    const closeMenuPage = () => {
      closeMenu(true)
    }

    // Register with MenuController for global access
    menuControllerUnregister = MenuController.register(
      closeMenuPage,
      () => isActive
    )

    const setScrollPosition = () => {
      gsap.set(document.body, {
        height: '100svh',
        overflow: 'hidden',
        // top: -Scroll.currentScroll,
        position: 'relative',
      })

      gsap.set(mainPage, {
        height: '100%',
      })
    }

    const resetScrollPosition = () => {
      document.body.style.removeProperty('height')
      document.body.style.removeProperty('overflow')
      document.body.style.removeProperty('position')

      mainPage?.style.removeProperty('transform')
      mainPage?.style.removeProperty('opacity')
      mainPage?.style.removeProperty('translate')
      mainPage?.style.removeProperty('scale')
      mainPage?.style.removeProperty('height')
      mainPage?.style.removeProperty('rotate')
    }

    triggerOpen?.addEventListener('click', toggleMenu)
    triggerClose?.addEventListener('click', toggleMenu)
    // close menu on escape key press
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isActive) {
        closeMenu()
      }
    })

    const disableTriggers = () => {
      triggerOpen?.classList.add('disabled')
      // triggerClose?.classList.add("disabled");
    }
    const enableTriggers = () => {
      triggerOpen?.classList.remove('disabled')
      // triggerClose?.classList.remove("disabled");
    }

    const cleanup = () => {
      triggerOpen?.removeEventListener('click', handleTriggerClick)
      triggerClose?.removeEventListener('click', handleTriggerClick)
      document.removeEventListener('keydown', handleTriggerClick)

      // Unregister from MenuController
      if (menuControllerUnregister) {
        menuControllerUnregister()
        menuControllerUnregister = null
      }

      // Kill any running animations
      if (currentTl) {
        currentTl.kill()
        currentTl = null
      }
    }

    ;(element as any)._menuCleanup = cleanup
  })

  onDestroy(() => {
    // Call cleanup if it exists
    const cleanup = (element as any)._menuCleanup
    if (cleanup) {
      cleanup()
    }
  })
}
